export type Vec2 = { x: number; y: number };

export function vec2(x = 0, y = 0): Vec2 {
  return { x, y };
}

export function len2(v: Vec2): number {
  return Math.hypot(v.x, v.y);
}

export function normalize2(v: Vec2): Vec2 {
  const length = len2(v);
  if (length < 1e-8) return { x: 0, y: 0 };
  return { x: v.x / length, y: v.y / length };
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export function dist2(a: Vec2, b: Vec2): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/**
 * Return the first normalized time at which a segment enters a planar circle.
 * A point-only projectile check can tunnel through small targets between 60 Hz
 * ticks, especially after velocity calibration. Keeping this helper planar is
 * intentional: world-Y is presentation-only combat data.
 */
export function segmentCircleHitFraction(
  start: Vec2,
  end: Vec2,
  center: Vec2,
  radius: number,
): number | null {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const ox = start.x - center.x;
  const oy = start.y - center.y;
  const radiusSq = radius * radius;

  if (ox * ox + oy * oy <= radiusSq) return 0;

  const lengthSq = dx * dx + dy * dy;
  if (lengthSq <= 1e-12) return null;

  const b = 2 * (ox * dx + oy * dy);
  const c = ox * ox + oy * oy - radiusSq;
  const discriminant = b * b - 4 * lengthSq * c;
  if (discriminant < 0) return null;

  const entry = (-b - Math.sqrt(discriminant)) / (2 * lengthSq);
  return entry >= 0 && entry <= 1 ? entry : null;
}

export function angleOf(v: Vec2): number {
  return Math.atan2(v.y, v.x);
}

export function fromAngle(angle: number): Vec2 {
  return { x: Math.cos(angle), y: Math.sin(angle) };
}

export function softClamp(value: number, min: number, max: number, band: number): number {
  if (value < min) {
    const overshoot = min - value;
    return value + overshoot * clamp(overshoot / band, 0.15, 1);
  }
  if (value > max) {
    const overshoot = value - max;
    return value - overshoot * clamp(overshoot / band, 0.15, 1);
  }
  return value;
}
